import { logger } from '@/utils/logger';
import { useTerms } from '@/queries/useTermQueries';
import { Term } from '@/types/term_type';
import { sanitizeURL } from '@/utils/xssProtection';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import CheckboxComponent from './CheckboxComponent';

interface AgreementSectionProps {
  onAgreementChange?: (allAgreed: boolean, agreements: AgreementState) => void;
  showAgeConfirmation?: boolean;
  context?: 'signup' | 'purchase';
  children?: React.ReactNode;
}

export interface AgreementState {
  terms: boolean;
  privacy: boolean;
  age?: boolean;
  productInfo?: boolean;
  personalInfo?: boolean;
}

export const AgreementSection: React.FC<AgreementSectionProps> = ({
  onAgreementChange,
  showAgeConfirmation = false,
  context = 'signup',
  children,
}) => {
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreements, setAgreements] = useState<AgreementState>({
    terms: false,
    privacy: false,
    age: false,
    productInfo: false,
    personalInfo: false,
  });

  // ✅ TanStack Query에서 약관 데이터 가져오기
  const { data: allTerms, isLoading } = useTerms();

  // ✅ 약관 데이터를 category 기반 객체로 변환
  const termsData = useMemo(() => {
    const termsMap: Record<string, Term> = {};

    allTerms?.forEach(term => {
      termsMap[term.category] = term;
    });

    return termsMap;
  }, [allTerms]);

  // useMemo로 checkboxConfig 메모이제이션
  const checkboxConfig = useMemo(() => {
    if (context === 'signup') {
      const config: Record<
        string,
        {
          label: string;
          modalKey?: string;
          termCategory?: string;
          required: boolean;
        }
      > = {
        terms: {
          label: 'Agree to Terms of Service (Required)',
          modalKey: 'TERMS_OF_SERVICE',
          termCategory: 'TERMS',
          required: true,
        },
        privacy: {
          label: 'Agree to Privacy Policy (Required)',
          modalKey: 'PRIVACY_POLICY',
          termCategory: 'PRIVACY',
          required: true,
        },
      };

      if (showAgeConfirmation) {
        config.age = {
          label: 'I am 14 years or older (Required)',
          required: true,
        };
      }

      return config;
    } else {
      return {
        productInfo: {
          label: '[Required] Agree to Product Information, Pricing, and Refund Policy',
          modalKey: 'TERMS_OF_SERVICE',
          termCategory: 'REFUND',
          required: true,
        },
        personalInfo: {
          label: '[Required] Agree to Collection and Use of Personal Information',
          modalKey: 'PRIVACY_POLICY',
          termCategory: 'PRIVACY',
          required: true,
        },
      };
    }
  }, [context, showAgeConfirmation]);

  // 전체 동의 체크 상태 업데이트
  useEffect(() => {
    const requiredKeys = Object.keys(checkboxConfig);
    const allChecked = requiredKeys.every(key => agreements[key as keyof AgreementState]);

    setAgreeAll(allChecked);

    if (onAgreementChange) {
      onAgreementChange(allChecked, agreements);
    }
  }, [agreements, checkboxConfig, onAgreementChange]);

  const handleAgreeAll = () => {
    const newValue = !agreeAll;
    const newAgreements = { ...agreements };

    Object.keys(checkboxConfig).forEach(key => {
      newAgreements[key as keyof AgreementState] = newValue;
    });

    setAgreements(newAgreements);
  };

  const handleAgreementToggle = (key: keyof AgreementState) => {
    setAgreements(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const openTerms = (config: { modalKey?: string; termCategory?: string }) => {
    if (config.termCategory) {
      const term = termsData[config.termCategory];

      if (!term) {
        logger.error(`Term data not found for category: ${config.termCategory}`);
        return;
      }

      // content_url이 있으면 새 창으로 열기 (sanitize URL to prevent attacks)
      if (term.content_url) {
        const sanitizedUrl = sanitizeURL(term.content_url);
        if (sanitizedUrl) {
          window.open(sanitizedUrl, '_blank');
        } else {
          logger.error('Invalid URL detected');
        }
      } else {
        logger.warn(`No content_url found for category: ${config.termCategory}`);
      }
      return;
    }
  };

  // ✅ 로딩 중일 때 처리 (선택사항)
  if (isLoading) {
    return (
      <Container>
        <LoadingPlaceholder>약관을 불러오는 중...</LoadingPlaceholder>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <CheckboxComponent
          id="agree-all"
          checked={agreeAll}
          onChange={handleAgreeAll}
          fontSize="14px"
          variant="selectAll"
        >
          Agree to All
        </CheckboxComponent>

        {Object.entries(checkboxConfig).map(([key, config]) => (
          <CheckboxComponent
            key={key}
            id={`agree-${key}`}
            checked={agreements[key as keyof AgreementState] || false}
            onChange={() => handleAgreementToggle(key as keyof AgreementState)}
            fontSize="12px"
            onViewClick={config.modalKey || config.termCategory ? () => openTerms(config) : undefined}
          >
            {config.label}
          </CheckboxComponent>
        ))}

        {children}
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  background: ${({ theme }) => theme.colors.bgGray[500]};
  border-radius: 10px;
`;

const LoadingPlaceholder = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 14px;
`;
